import { BodyCellRenderResult, Column, RowTypeBase } from '@qre-mailing/smart-table';

import type { QuotaFlatStructure } from '../../api/hooks/queries/useQuotaFlatStructure';

const lowercaseFirstLetter = (s: string): string => (s.length > 0 ? `${s.charAt(0).toLowerCase()}${s.slice(1)}` : s);

const renderDisplay = (value: string | undefined, label: string | undefined): string => {
  if (value === undefined || value === null || value === '') return '';
  return label ? `${value}: ${label}` : value;
};

const quotaMultipliersByDepth: Record<number, number[]> = {
  1: [1],
  2: [1000, 1],
  3: [1000000, 1000, 1],
};

const parseNestedQuotaPackedDigits = (valueStr: string, n: number): number[] => {
  if (n <= 0) throw new Error(`n must be positive, got ${n}`);
  const digits = String(valueStr).replace(/\D/g, '');
  if (digits.length === 0) throw new Error('nested quota value has no digits');
  if (n === 1) return [Number.parseInt(digits, 10)];

  const end = digits.length;
  const cut = Math.max(0, end - 3 * (n - 1));
  const prefix = digits.slice(0, cut);
  const restStr = digits.slice(cut);
  if (restStr.length === 0) throw new Error('nested quota value parse failed');

  const chunks = restStr.match(/.{1,3}/g) ?? [];
  const needed = n - 1;
  const extra = chunks.length - needed;
  const extraChunks = chunks.slice(0, Math.max(0, extra));
  const lastChunks = chunks.slice(Math.max(0, extra));
  const prefixStar = `${prefix}${extraChunks.join('')}`;
  const parts = [prefixStar, ...lastChunks];

  if (parts.length !== n || parts.some((p) => p.length === 0)) {
    throw new Error('nested quota value parse failed');
  }

  return parts.map((p) => Number.parseInt(p, 10));
};

const packNestedQuotaValue = (parts: number[]): number | undefined => {
  const multipliers = quotaMultipliersByDepth[parts.length];
  if (!multipliers) return undefined;
  return parts.reduce((acc, p, i) => acc + p * multipliers[i], 0);
};

const getQuotaLabel = (
  lookupForField: Record<string, string> | undefined,
  fieldName: string,
  value: string | undefined
): {
  label?: string;
} => {
  if (!lookupForField || value === undefined) return {};

  const direct = lookupForField[value];
  if (direct) return { label: direct };

  const depth = fieldName.split('__').length;
  if (!quotaMultipliersByDepth[depth]) return {};

  let decoded: number[];
  try {
    decoded = parseNestedQuotaPackedDigits(value, depth);
  } catch {
    return {};
  }

  const tryPacked = (parts: number[]): string | undefined => {
    const packed = packNestedQuotaValue(parts);
    if (packed === undefined) return undefined;
    const key = String(packed);
    return lookupForField[key] ? key : undefined;
  };

  // Try the raw decoded parts first (in case the stored value is already position-based).
  const directPackedKey = tryPacked(decoded);
  if (directPackedKey) {
    return { label: lookupForField[directPackedKey] };
  }

  // Generic normalization: subtract 100 from one chunk at a time (bounded by depth<=3).
  for (let i = 0; i < decoded.length; i++) {
    const v = decoded[i];
    const candidate = v - 100;
    if (candidate < 0 || candidate > 999) continue;
    const parts = decoded.map((x, idx) => (idx === i ? candidate : x));
    const key = tryPacked(parts);
    if (key) {
      return { label: lookupForField[key] };
    }
  }

  return {};
};

/**
 * Builds a bodyCellRenderer that shows `${val} - ${label}` for quota columns,
 * where labels come from a (col -> val -> label) lookup map returned by
 * `GET /survey/{qreId}/quota-flat-structure`.
 *
 * Row data is keyed by `column.fieldKey` (same convention as the default smart-table
 * renderer). Lookup is keyed by `column.fieldName` (the BE-side quota column name,
 * e.g. "sex", "sex__age", "sex__age__edu").
 *
 * Falls back to the raw value when a label is not available (unknown code,
 * stale data, etc.), so rows never break.
 */
export const createQuotaColumnRenderer =
  (quotaLookup: QuotaFlatStructure) =>
  <RowType extends RowTypeBase>(row: RowType, column: Column<RowType>): BodyCellRenderResult => {
    const { fieldName, fieldKey } = column;
    const rowKey = fieldKey ?? lowercaseFirstLetter(fieldName);
    const rawValue = (row as Record<string, unknown>)[rowKey];
    const value = rawValue === null || rawValue === undefined ? undefined : String(rawValue);

    const lookupForField = quotaLookup?.[fieldName] as Record<string, string> | undefined;
    const { label } = getQuotaLabel(lookupForField, fieldName, value);
    const display = renderDisplay(value, label);

    return {
      node: <span>{display}</span>,
      value: display,
    };
  };
